import Link from "next/link";

import { useCallback, useMemo } from "react";

import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";

import AccountUpgradeModal from "components/AccountUpgradeModal";
import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { PermissionKey } from "components/ToolsPageLayout/ToolsPageTabs";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";
import highlightText from "utils/highLightText";

const ToolSnippet = ({
  tool,
  className,
  iconClassName,
  titleClassName,
  badgeClassName,
  query,
  hideDescription,
  hideBorder,
  linkTarget,
  style,
}: {
  tool: any;
  className?: string;
  iconClassName?: string;
  titleClassName?: string;
  badgeClassName?: string;
  query?: string;
  hideDescription?: boolean;
  hideBorder?: boolean;
  linkTarget?: string;
  style?: "default" | "small";
}) => {
  const { account, interfaceSetting, mutateAccount } = useAuthentication();
  const { toggleModal } = useSignInSignUp();
  const [showStripeModal, toggleStripeModal] = useToggle();

  const isAvailable = useMemo(() => {
    if (account) {
      return tool?.access?.some(
        (access: any) => account[access as PermissionKey]
      );
    }

    return tool.public;
  }, [account, tool.access, tool.public]);

  const shouldHide = useMemo(() => {
    if (account) {
      if (account.isPreviewPass) {
        return false;
      }
      return tool.hideOnRestricted && !isAvailable;
    }

    return false;
  }, [account, isAvailable, tool.hideOnRestricted]);

  const link = useMemo(() => {
    if (!isAvailable) return "#";

    return (tool.href || tool.link)!;
  }, [isAvailable, tool.href, tool.link]);

  const onClick = useCallback(
    async (event: any) => {
      if (tool.activityKey) {
        await baseFetcher(`/api/v2/tools/${tool.id}`);
      }

      if (!isAvailable && !tool.paidOnly) {
        event.preventDefault();
        toggleModal("sign-up");
      } else if (!isAvailable && tool.paidOnly) {
        event.preventDefault();
        toggleStripeModal();
      } else if (tool.withBrowserNavigation) {
        event.preventDefault();

        location.href = link;
      }

      const lastVisitedTools = {
        lastVisitedTools: [
          tool.id,
          ...(interfaceSetting?.lastVisitedTools || []).filter(
            (t: any) => t !== tool.id
          ),
        ].slice(0, 5),
      };

      await mutateAccount(
        {
          ...account,
          interfaceSetting: { ...interfaceSetting, ...lastVisitedTools },
        },
        { revalidate: false }
      );
    },
    [
      account,
      interfaceSetting,
      isAvailable,
      link,
      mutateAccount,
      toggleModal,
      toggleStripeModal,
      tool.activityKey,
      tool.id,
      tool.paidOnly,
      tool.withBrowserNavigation,
    ]
  );

  if (shouldHide) return null;

  return (
    <>
      <Link
        href={link}
        key={tool.id}
        className={twMerge(
          style === "small"
            ? "basis-full lg:basis-auto"
            : "basis-full grow lg:grow-0 lg:basis-[calc(25%-15px)]"
        )}
        rel={tool.rel}
        scroll={false}
        target={linkTarget || tool.target}
        onClick={onClick}
      >
        {/* This is to fix issue with override the default fill color for SVG icons with tailwind nơt working */}
        <div className="[&>g]:!fill-white"></div>
        <CustomCard
          className={twMerge(
            "flex items-center h-full",
            "mb-0 py-[10px] px-[15px] rounded-[12px] w-[100%]",
            style === "small" && "pl-[10px] pr-[14px] py-[8px]",
            hideBorder
              ? ""
              : "shadow-none border-[2px] border-solid border-[#e4eaf0]",
            "hover:text-black hover:border-smart-teal hover:shadow-[0_6px_20px_rgba(36,169,167,0.15),_0_2px_6px_rgba(0,0,0,0.06)]",
            hideBorder && "hover:shadow-none",
            className
          )}
        >
          <div className="flex flex-col md:items-center md:justify-between flex-1">
            {!isAvailable && (
              <div
                className={twMerge(
                  "flex flex-1 justify-start w-full",
                  "right-[-7px]"
                )}
              >
                <div
                  className={twMerge(
                    "gap-[3px] flex items-center p-[3px] px-[6px] mb-[10px] bg-inactive-gray rounded-full text-[8px]",
                    tool.paidOnly && "bg-mellow-yellow/50"
                  )}
                >
                  <FontAwesomeIcon icon={faLock} className="text-[8px]" />
                  <span>
                    {tool.paidOnly ? "Paid Plans Only" : "Sign Up to Use"}
                  </span>
                </div>
              </div>
            )}
            <div className={twMerge("flex flex-1 max-w-none w-full")}>
              <div>
                <div
                  className={twMerge(
                    tool.className,
                    "min-w-[38px] min-h-[38px] rounded-[5px]",
                    style === "small" && "min-w-[28px] min-h-[28px]",
                    "text-white text-[20px]",
                    "flex items-center justify-center",
                    "relative overflow-hidden",
                    "mr-[12px] shadow-[0_2px_15px_rgba(0,0,0,0.15)]",
                    iconClassName
                  )}
                >
                  {tool.icon}
                </div>
              </div>

              <div
                className={twMerge(
                  (style === "small" || hideDescription) && "flex items-center"
                )}
              >
                <div
                  className={twMerge("font-bold text-[13px]", titleClassName)}
                >
                  {query ? highlightText(tool.title, query) : tool.title}
                </div>
                {!hideDescription && style !== "small" && (
                  <div
                    className={twMerge(
                      "text-gray-gray text-[11.5px] line-clamp-2",
                      !isAvailable && "min-h-[34.5px]"
                    )}
                  >
                    {query
                      ? highlightText(tool.description, query)
                      : tool.description}
                  </div>
                )}
              </div>

              {tool.isBeta && (
                <div
                  className={twMerge(
                    "bg-bearish-red",
                    "text-white text-[9px] font-[900] leading-[1]",
                    "py-[4px] px-[6px]",
                    "rounded-[5px] border border-solid border-white",
                    "shadow-[0_2px_6px_rgba(168,85,247,0.4)]",
                    "absolute top-[-7px] right-[-7px]",
                    badgeClassName
                  )}
                >
                  BETA
                </div>
              )}
              {tool.isNew && (
                <div
                  className={twMerge(
                    "bg-ink-pink",
                    "text-white text-[9px] font-[900] leading-[1]",
                    "py-[4px] px-[6px]",
                    "rounded-[5px] border border-solid border-white",
                    "shadow-[0_2px_6px_rgba(168,85,247,0.4)]",
                    "absolute top-[-7px] right-[-7px]",
                    badgeClassName
                  )}
                >
                  NEW
                </div>
              )}
            </div>
          </div>
        </CustomCard>
      </Link>
      {showStripeModal && <AccountUpgradeModal onClose={toggleStripeModal} />}
    </>
  );
};

export default ToolSnippet;
